import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo, useEffect } from 'react'
import { marked } from 'marked'
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@smorgrav/velpr-ui'
import { db } from '@/firebase'
import { collection, doc, setDoc } from 'firebase/firestore'
import { useSubscribers } from '@/hooks/useSubscribers'
import { useSites, useManagersForSites } from '@/hooks/useSiteManagers'
import {
  useTemplates,
  saveTemplate,
  deleteTemplate,
  type MailTemplate
} from '@/hooks/useTemplates'
import { usePlatformMails, type PlatformMail } from '@/hooks/usePlatformMails'
import type { SubscriptionTopic } from '@smorgrav/hundehall_shared'

interface Recipient {
  email: string
  name: string
  siteName?: string
  uid?: string
}

type AudienceType = 'newsletter' | 'serviceinfo' | 'sitemanagers'
type TabType = 'compose' | 'templates' | 'history'

export const Route = createFileRoute('/email')({
  component: EmailPage
})

function EmailPage() {
  const [tab, setTab] = useState<TabType>('compose')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  const loadTemplate = (t: MailTemplate) => {
    setSubject(t.subject)
    setBody(t.body)
    setTab('compose')
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="text-2xl font-bold">E-post</h1>

      <div className="border-b">
        <nav className="-mb-px flex gap-2">
          <TabButton
            active={tab === 'compose'}
            onClick={() => setTab('compose')}
          >
            Skriv
          </TabButton>
          <TabButton
            active={tab === 'templates'}
            onClick={() => setTab('templates')}
          >
            Maler
          </TabButton>
          <TabButton
            active={tab === 'history'}
            onClick={() => setTab('history')}
          >
            Historikk
          </TabButton>
        </nav>
      </div>

      {tab === 'compose' && (
        <ComposeTab
          subject={subject}
          body={body}
          setSubject={setSubject}
          setBody={setBody}
        />
      )}
      {tab === 'templates' && <TemplatesTab onLoad={loadTemplate} />}
      {tab === 'history' && <HistoryTab />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-primary text-foreground'
          : 'text-muted-foreground hover:text-foreground border-transparent'
      }`}
    >
      {children}
    </button>
  )
}

interface ComposeTabProps {
  subject: string
  body: string
  setSubject: (v: string) => void
  setBody: (v: string) => void
}

function ComposeTab({ subject, body, setSubject, setBody }: ComposeTabProps) {
  const [audience, setAudience] = useState<AudienceType>('newsletter')
  const [selectedSites, setSelectedSites] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)

  const subscriberTopic: SubscriptionTopic | null =
    audience === 'newsletter' || audience === 'serviceinfo' ? audience : null

  const { subscribers, loading: subscribersLoading } =
    useSubscribers(subscriberTopic)
  const { sites, loading: sitesLoading } = useSites()

  const selectedSiteInfos = useMemo(
    () => sites.filter((s) => selectedSites.includes(s.id)),
    [sites, selectedSites]
  )
  const { managers, loading: managersLoading } = useManagersForSites(
    audience === 'sitemanagers' ? selectedSiteInfos : []
  )

  const [excluded, setExcluded] = useState<Set<string>>(new Set())

  useEffect(() => {
    setExcluded(new Set())
  }, [audience])

  const renderedHtml = useMemo(() => {
    if (!body) return ''
    return marked(body) as string
  }, [body])

  const previewDoc = useMemo(
    () => wrapInTemplate(subject || '(uten emne)', renderedHtml, audience),
    [subject, renderedHtml, audience]
  )

  const baseRecipients: Recipient[] = useMemo(() => {
    if (audience === 'sitemanagers') {
      return managers.map((m) => ({
        email: m.email,
        name: m.name,
        siteName: m.siteName,
        uid: m.uid
      }))
    }
    return subscribers.map((s) => ({
      email: s.email,
      name: s.name,
      uid: s.uid
    }))
  }, [audience, managers, subscribers])

  const dedupedRecipients = useMemo(() => {
    const seen = new Set<string>()
    return baseRecipients.filter((r) => {
      if (!r.email || seen.has(r.email)) return false
      seen.add(r.email)
      return true
    })
  }, [baseRecipients])

  const finalRecipients = useMemo(
    () => dedupedRecipients.filter((r) => !excluded.has(r.email)),
    [dedupedRecipients, excluded]
  )

  const recipientsLoading =
    audience === 'sitemanagers' ? managersLoading : subscribersLoading

  const toggleExcluded = (email: string) => {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  const handleSend = async () => {
    if (!subject || !body || finalRecipients.length === 0) return

    setSending(true)
    try {
      const now = new Date().toISOString()
      const mailId = `newsletter-${Date.now()}`
      const mailRef = doc(collection(db, 'platformmail'), mailId)

      const toEntries = finalRecipients.map((r) => {
        const entry: { name: string; email: string; uid?: string } = {
          name: r.name,
          email: r.email
        }
        if (r.uid) entry.uid = r.uid
        return entry
      })

      const docData: Record<string, unknown> = {
        id: mailId,
        to: toEntries,
        from: { name: 'Velpr', email: 'post@velpr.online' },
        replyto: { name: 'Velpr', email: 'post@velpr.online' },
        subject,
        body: wrapInTemplate(subject, renderedHtml, audience),
        siteId: '_platform',
        provider: 'default',
        state: 'waiting',
        lastUpdated: now,
        createdAt: now,
        retryCount: 0,
        delivery: []
      }
      if (audience === 'newsletter' || audience === 'serviceinfo') {
        docData.unsubscribeTopic = audience
      }

      await setDoc(mailRef, docData)

      setSent(true)
    } catch (err) {
      console.error('Failed to send', err)
      alert('Sending feilet. Se konsollen for detaljer.')
    } finally {
      setSending(false)
    }
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return
    setSavingTemplate(true)
    try {
      await saveTemplate({
        name: templateName.trim(),
        subject,
        body
      })
      setSaveOpen(false)
      setTemplateName('')
    } catch (err) {
      console.error('Failed to save template', err)
      alert('Kunne ikke lagre malen. Se konsollen for detaljer.')
    } finally {
      setSavingTemplate(false)
    }
  }

  if (sent) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">E-post sendt</h2>
        <p className="text-muted-foreground">
          E-posten er lagt i kø og vil bli sendt til {finalRecipients.length}{' '}
          mottaker(e).
        </p>
        <Button
          onClick={() => {
            setSent(false)
            setSubject('')
            setBody('')
          }}
        >
          Send ny e-post
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Audience selection */}
      <div className="space-y-3">
        <label className="text-sm font-medium">Mottakere</label>
        <div className="flex gap-3">
          {(
            [
              ['newsletter', 'Nyhetsbrev-abonnenter'],
              ['serviceinfo', 'Driftsinfo-abonnenter'],
              ['sitemanagers', 'Anleggsansvarlige']
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className={`flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm ${
                audience === value
                  ? 'border-primary bg-primary/5 font-medium'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <input
                type="radio"
                name="audience"
                value={value}
                checked={audience === value}
                onChange={() => setAudience(value)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </div>

        {audience === 'sitemanagers' && (
          <div className="max-h-48 overflow-y-auto rounded-md border p-2">
            {sitesLoading ? (
              <p className="text-sm text-muted-foreground">Laster anlegg...</p>
            ) : (
              sites.map((site) => (
                <label
                  key={site.id}
                  className="flex items-center gap-2 rounded px-2 py-1 hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selectedSites.includes(site.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSites((prev) => [...prev, site.id])
                      } else {
                        setSelectedSites((prev) =>
                          prev.filter((id) => id !== site.id)
                        )
                      }
                    }}
                  />
                  <span className="text-sm">{site.name}</span>
                </label>
              ))
            )}
          </div>
        )}

        {/* Recipient list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {recipientsLoading
                ? 'Laster mottakere...'
                : excluded.size > 0
                  ? `${finalRecipients.length} av ${dedupedRecipients.length} mottaker(e)`
                  : `${finalRecipients.length} mottaker(e)`}
            </p>
            {excluded.size > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setExcluded(new Set())}
              >
                Tilbakestill ekskluderte
              </Button>
            )}
          </div>

          {dedupedRecipients.length > 0 && (
            <div className="max-h-64 overflow-y-auto rounded-md border">
              {dedupedRecipients.map((r) => {
                const isExcluded = excluded.has(r.email)
                return (
                  <div
                    key={r.email}
                    className={`flex items-center justify-between gap-2 border-b px-3 py-1.5 text-sm last:border-b-0 ${
                      isExcluded ? 'opacity-50' : ''
                    }`}
                  >
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-medium">{r.name || r.email}</span>
                      {r.name && (
                        <span className="text-muted-foreground ml-2">
                          {r.email}
                        </span>
                      )}
                      {r.siteName && (
                        <span className="text-muted-foreground ml-2">
                          · {r.siteName}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleExcluded(r.email)}
                      className="text-muted-foreground hover:text-foreground shrink-0 text-xs"
                    >
                      {isExcluded ? 'Inkluder' : 'Ekskluder'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Subject */}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="subject">
          Emne
        </label>
        <Input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="E-postemne"
        />
      </div>

      {/* Markdown editor + preview */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Innhold (Markdown)</label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Skjul forhåndsvisning' : 'Vis forhåndsvisning'}
          </Button>
        </div>

        <div className={showPreview ? 'grid grid-cols-2 gap-4' : ''}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Skriv innholdet i Markdown-format..."
            rows={16}
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 font-mono text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          {showPreview && (
            <iframe
              title="E-post forhåndsvisning"
              srcDoc={previewDoc}
              sandbox=""
              className="bg-background h-[400px] w-full rounded-md border"
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleSend}
          disabled={
            sending || !subject || !body || finalRecipients.length === 0
          }
        >
          {sending
            ? 'Sender...'
            : `Send til ${finalRecipients.length} mottaker(e)`}
        </Button>
        <Button
          variant="outline"
          onClick={() => setSaveOpen(true)}
          disabled={!subject && !body}
        >
          Lagre som mal
        </Button>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lagre som mal</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="template-name">
              Mal-navn
            </label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="f.eks. Vårnyhetsbrev"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={!templateName.trim() || savingTemplate}
            >
              {savingTemplate ? 'Lagrer...' : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface TemplatesTabProps {
  onLoad: (t: MailTemplate) => void
}

function TemplatesTab({ onLoad }: TemplatesTabProps) {
  const { templates, loading } = useTemplates()

  const handleDelete = async (t: MailTemplate) => {
    if (!confirm(`Slette malen "${t.name}"?`)) return
    try {
      await deleteTemplate(t.id)
    } catch (err) {
      console.error('Failed to delete template', err)
      alert('Kunne ikke slette malen.')
    }
  }

  if (loading) {
    return <p className="text-muted-foreground text-sm">Laster maler...</p>
  }

  if (templates.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        Ingen maler enda. Lagre en mal fra Skriv-fanen.
      </p>
    )
  }

  return (
    <div className="divide-y rounded-md border">
      {templates.map((t) => (
        <div key={t.id} className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium">{t.name}</div>
            <div className="text-muted-foreground truncate text-sm">
              {t.subject || '(uten emne)'}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onLoad(t)}>
            Bruk
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(t)}
            className="text-destructive hover:text-destructive"
          >
            Slett
          </Button>
        </div>
      ))}
    </div>
  )
}

function HistoryTab() {
  const { mails, loading } = usePlatformMails(50)
  const [selected, setSelected] = useState<PlatformMail | null>(null)

  if (loading) {
    return <p className="text-muted-foreground text-sm">Laster historikk...</p>
  }

  if (mails.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        Ingen e-post er sendt enda.
      </p>
    )
  }

  return (
    <>
      <div className="divide-y rounded-md border">
        {mails.map((m) => (
          <button
            type="button"
            key={m.id}
            onClick={() => setSelected(m)}
            className="hover:bg-muted/40 flex w-full items-center gap-3 px-4 py-3 text-left"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium">{m.subject || '(uten emne)'}</div>
              <div className="text-muted-foreground text-sm">
                {m.recipientCount} mottaker(e) · {formatDate(m.createdAt)}
              </div>
            </div>
            <StateBadge state={m.state} />
          </button>
        ))}
      </div>

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selected?.subject || '(uten emne)'}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="text-muted-foreground flex items-center gap-3 text-sm">
                <span>{selected.recipientCount} mottaker(e)</span>
                <span>·</span>
                <span>{formatDate(selected.createdAt)}</span>
                <StateBadge state={selected.state} />
              </div>
              <iframe
                title="E-post forhåndsvisning"
                srcDoc={selected.body}
                sandbox=""
                className="bg-background h-[500px] w-full rounded-md border"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function StateBadge({ state }: { state: string }) {
  const tone =
    state === 'sent'
      ? 'bg-green-100 text-green-800'
      : state === 'failed'
        ? 'bg-red-100 text-red-800'
        : state === 'sending' || state === 'waiting'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-muted text-muted-foreground'
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {state}
    </span>
  )
}

function formatDate(iso: string): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString('nb-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return iso
  }
}

function wrapInTemplate(
  subject: string,
  bodyHtml: string,
  audience: AudienceType
): string {
  const footerHtml =
    audience === 'sitemanagers'
      ? `<p>Du mottar denne e-posten fordi du står oppført som ansvarlig for et anlegg hos Velpr.</p>`
      : `<p>Du mottar denne e-posten fordi du abonnerer på oppdateringer fra Velpr.</p>
      <p><a href="{{UNSUBSCRIBE_URL}}">Meld av / administrer abonnement</a></p>`
  return `<!DOCTYPE html>
<html lang="no">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${subject}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 16px;
      line-height: 1.6;
      color: #333;
      background-color: #f4f4f4;
    }
    .email-container {
      max-width: 600px;
      margin: 20px auto;
      background: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .email-header {
      background: #ffffff;
      padding: 32px 32px 24px;
      text-align: center;
      border-bottom: 3px solid #c8541d;
    }
    .email-header img {
      display: inline-block;
      height: 64px;
      width: auto;
      border: 0;
    }
    .email-body {
      padding: 32px;
    }
    .email-body h1, .email-body h2, .email-body h3 {
      color: #1a1a2e;
    }
    .email-body a {
      color: #c8541d;
    }
    .email-body img {
      max-width: 100%;
      height: auto;
    }
    .email-footer {
      padding: 24px 32px;
      text-align: center;
      font-size: 13px;
      color: #888;
      border-top: 1px solid #eee;
    }
    .email-footer a {
      color: #c8541d;
    }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="email-header">
      <img src="https://www.velpr.online/logo.png" alt="Velpr" />
    </div>
    <div class="email-body">
      ${bodyHtml}
    </div>
    <div class="email-footer">
      ${footerHtml}
    </div>
  </div>
</body>
</html>`
}
